<!--
  Ordini PM — list of custom-made / made-to-measure orders with
  scadenza timer. Redesigned 2026-06-10 (v2.6.0, Phase 1.5 Chunk A).

  Drops kendo-grid + kendo-datepicker + mat-spinner. The page has
  exactly one job a regular order list doesn't: surface the
  per-order deadline countdown and the 24-month progress bar.
  Everything else (assign action, search, date range) reuses the
  Diakonix table-card pattern from the agent / supplier families.

  Click on a row opens the legacy `<app-orderscm-det>` assign modal
  via the existing `openModal.isStorico` flag — that sub-component
  is unchanged in this chunk.
-->

<div class="pm-page">

  <!-- ─── Sticky header ────────────────────────────────────────── -->
  <header class="pm-header">
    <div class="pm-header__titles">
      <h1 class="pm-header__title">⏳ Ordini PM</h1>
      <p class="pm-header__subtitle">
        @if (filteredOrders.length > 0) {
          <strong>{{ filteredOrders.length }}</strong>
          {{ filteredOrders.length === 1 ? 'ordine custom' : 'ordini custom' }}
          @if (filterTerm) { · filtro <strong>"{{ filterTerm }}"</strong> }
        } @else if (loading) {
          Caricamento…
        } @else {
          Nessun ordine custom in lista
        }
      </p>
    </div>

    <button
      type="button"
      class="refresh-btn"
      (click)="reloadFunction()"
      title="Aggiorna lista"
      aria-label="Aggiorna lista"
    >🔁</button>
  </header>

  <!-- ─── Filter bar ───────────────────────────────────────────── -->
  <section class="pm-filters" aria-label="Filtri ricerca">
    <div class="filter-field">
      <label class="filter-field__label" for="searchOrderPm">Cerca ordine</label>
      <div class="filter-field__input-wrap">
        <input
          id="searchOrderPm"
          type="search"
          class="filter-field__input"
          placeholder="N° ordine o cliente…"
          [(ngModel)]="filterTerm"
          (ngModelChange)="handleFilterChange()"
          (keyup.enter)="searchOrders()"
          (input)="onInputChanged($event)"
        />
        @if (filterTerm) {
          <button
            type="button"
            class="filter-field__clear"
            (click)="clearSearch()"
            aria-label="Rimuovi filtro"
          >×</button>
        }
      </div>
    </div>

    <div class="filter-field filter-field--dates">
      <label class="filter-field__label">Data ordine</label>
      <div class="filter-field__date-pair">
        <input
          type="date"
          class="filter-field__input filter-field__input--date"
          [(ngModel)]="startDateStr"
          aria-label="Data da"
        />
        <span class="filter-field__date-sep">→</span>
        <input
          type="date"
          class="filter-field__input filter-field__input--date"
          [(ngModel)]="endDateStr"
          aria-label="Data a"
        />
      </div>
    </div>

    <button
      type="button"
      class="btn btn--primary"
      (click)="searchOrders()"
      [disabled]="loading"
    >🔍 Cerca</button>
  </section>

  <!-- ─── Table card ───────────────────────────────────────────── -->
  <section class="pm-table-card" aria-label="Elenco ordini PM">

    @if (loading) {
      <div class="table-skeleton" aria-busy="true">
        @for (i of [1, 2, 3, 4, 5, 6]; track i) {
          <div class="table-skeleton__row">
            <div class="table-skeleton__line table-skeleton__line--lg"></div>
            <div class="table-skeleton__line table-skeleton__line--md"></div>
            <div class="table-skeleton__line table-skeleton__line--sm"></div>
          </div>
        }
      </div>
    } @else if (filteredOrders.length === 0) {
      <div class="pm-empty">
        <div class="pm-empty__icon" aria-hidden="true">
          @if (filterTerm) { 🔎 } @else { 📭 }
        </div>
        <p class="pm-empty__msg">
          @if (filterTerm) {
            Nessun ordine PM corrisponde a <strong>"{{ filterTerm }}"</strong>.
          } @else {
            Nessun ordine custom in coda.
          }
        </p>
      </div>
    } @else {
      <div class="pm-table-scroll">
        <table
          class="pm-table"
          role="table"
        >
          <thead>
            <tr>
              <th class="col-order is-sortable" (click)="sortBy('or_order_number')">
                N° ordine
                @if (sortColumn === 'or_order_number') {
                  <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                }
              </th>
              <th class="col-customer is-sortable" (click)="sortBy('or_customer')">
                Cliente
                @if (sortColumn === 'or_customer') {
                  <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                }
              </th>
              <th class="col-customer is-sortable" (click)="sortBy('or_days_remaining')">
                Scadenza
                @if (sortColumn === 'or_days_remaining') {
                  <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                }
              </th>
              <th class="col-date is-sortable" (click)="sortBy('or_order_date')">
                Data ordine
                @if (sortColumn === 'or_order_date') {
                  <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                }
              </th>
              <th class="col-date is-sortable" (click)="sortBy('or_articles_number')">
                N° articoli
                @if (sortColumn === 'or_articles_number') {
                  <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                }
              </th>
              <th class="col-actions" aria-label="Assegna"></th>
            </tr>
          </thead>
          <tbody>
            @for (
              item of filteredOrders
                | byToDate: [filters.startingDate, filters.endDate, filters.dateOrginizedBy.value];
              track item.or_order_number + '-' + ($index)
            ) {
              <tr
                class="pm-row"
                [attr.data-state]="deadlineState(item)"
                (click)="assignPM(item.or_gamma_id)"
                title="Assegna ordine"
              >
                <td class="col-order">
                  <span class="cell-order-num">{{ item.or_order_number || '—' }}</span>
                </td>
                <td class="col-customer">
                  <span class="cell-customer">{{ item.or_customer || '—' }}</span>
                </td>
                <td class="col-deadline">
                  <div class="deadline-cell">
                    <div class="deadline-cell__top">
                      <span
                        class="deadline-cell__days"
                        [attr.data-state]="deadlineState(item)"
                      >
                        @if (isExpired(item.or_order_date)) {
                          ⚠️ Scaduto
                        } @else {
                          {{ daysRemaining(item) }}
                          {{ daysRemaining(item) === 1 ? 'giorno' : 'giorni' }}
                        }
                      </span>
                      <span class="deadline-cell__date">
                        {{ deadline(item) | date: 'dd/MM/yyyy' }}
                      </span>
                    </div>
                    <div class="deadline-cell__bar">
                      <div
                        class="deadline-cell__fill"
                        [style.width.%]="progress(item.or_order_date)"
                        [attr.data-state]="deadlineState(item)"
                      ></div>
                    </div>
                  </div>
                </td>
                <td class="col-date">
                  <span class="cell-date">{{ item.or_order_date ? (item.or_order_date | date: 'dd/MM/yy') : '—' }}</span>
                </td>
                <td class="col-num">
                  <span class="cell-num">{{ item.or_articles_number ?? 0 }}</span>
                </td>
                <td class="col-actions" (click)="$event.stopPropagation()">
                  <button
                    type="button"
                    class="icon-btn"
                    title="Assegna ordine"
                    (click)="assignPM(item.or_gamma_id)"
                  >✏️</button>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }
  </section>

</div>

<!-- ── Assign modal (preserved legacy child component) ────────── -->
@if (openModalRef.isStorico) {
  <app-orderscm-det
    [opened]="openModalRef.isStorico"
    [order_id]="clickedOrder.toString()"
    (pingBackForBoolean)="onChildClose($event)"
  ></app-orderscm-det>
}
